import { FormSection } from "@/components/valuation/form-sections/FormSection";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels, carTestLabels } from "@/i18n";
import { VibrationsSection } from "@/components/car-post-ad/form-sections/VibrationsSection";
import { LeaksSection } from "@/components/car-post-ad/form-sections/LeaksSection";
import { NoisesSection } from "@/components/car-post-ad/form-sections/NoisesSection";
import { GearboxSection } from "@/components/car-post-ad/form-sections/GearboxSection";
import { CrashSection } from "@/components/car-post-ad/form-sections/CrashSection";
import { ACSection } from "@/components/car-post-ad/form-sections/ACSection";
import { DashboardSection } from "@/components/car-post-ad/form-sections/DashboardSection";
import { WindshieldSection } from "@/components/car-post-ad/form-sections/WindshieldSection";
import { BrakesSection } from "@/components/car-post-ad/form-sections/BrakesSection";
import { TireDepthSection } from "@/components/car-post-ad/form-sections/TireDepthSection";
import SpeedSection from "../form-sections/SpeedSection";
import { ModalInfoVehicleTest } from "../components/ModalInfoVehicleTest";

interface PostAnAdFormSectionsProps {
  testData: any;
  openSections: any;
  handleTestChange: (field: string, value: any) => void;
  handleEditSection: (section: string, event: React.MouseEvent) => void;
  handleNext: (section: string) => void;
  onNext: () => void;
  isCreatingTest?: boolean;
  isEditMode?: boolean;
  vehicleDetails?: { extraTireSet?: boolean };
  onVehicleDetailsChange?: (details: { extraTireSet?: boolean }) => void;
}

export const PostAnAdFormSections = ({
  testData,
  openSections,
  handleTestChange,
  handleEditSection,
  handleNext,
  onNext,
  isCreatingTest = false,
  isEditMode = false,
  vehicleDetails,
  onVehicleDetailsChange,
}: PostAnAdFormSectionsProps) => {
  const { language } = useLanguage();
  const lp = listingPostLabels(language);
  const ct = carTestLabels(language);
  const s = ct.pdf?.labels ?? ({} as Record<string, string>);
  const v = ct.pdf?.values ?? ({} as Record<string, string>);
  const opt =
    (v as { optionValues?: Record<string, string> }).optionValues ?? {};
  const getOptionDisplay = (val: string | undefined) =>
    val ? (opt[val] ?? val) : "";
  // Completion logic for each section
  const isSpeedCompleted = !!testData.speed;
  const isVibrationsCompleted =
    testData.vibrations?.problem === "no" ||
    (testData.vibrations?.problem === "yes" &&
      testData.vibrations?.location &&
      (testData.vibrations.location !== "Annat" ||
        (testData.vibrations.location === "Annat" &&
          testData.vibrations.other &&
          testData.vibrations.other.trim())));
  const isLeaksCompleted =
    testData.leaks?.problem === "no" ||
    (testData.leaks?.problem === "yes" &&
      testData.leaks?.source &&
      (testData.leaks.source !== "Annat" ||
        (testData.leaks.source === "Annat" &&
          testData.leaks.other &&
          testData.leaks.other.trim())));
  const isNoisesCompleted =
    testData.noises?.problem === "no" ||
    (testData.noises?.problem === "yes" &&
      testData.noises?.type &&
      (testData.noises.type !== "Annat" ||
        (testData.noises.type === "Annat" &&
          testData.noises.other &&
          testData.noises.other.trim())));
  const isGearboxCompleted =
    testData.gearbox?.problem === "no" ||
    (testData.gearbox?.problem === "yes" &&
      testData.gearbox?.issue &&
      (testData.gearbox.issue !== "Annat" ||
        (testData.gearbox.issue === "Annat" &&
          testData.gearbox.other &&
          testData.gearbox.other.trim())));
  const isCrashCompleted =
    testData.crash?.problem === "no" ||
    testData.crash?.problem === "unknown" ||
    (testData.crash?.problem === "yes" &&
      testData.crash?.area &&
      (testData.crash.area !== "Annat" ||
        (testData.crash.area === "Annat" &&
          testData.crash.other &&
          testData.crash.other.trim())));
  const isACCompleted =
    testData.ac?.problem === "no" ||
    testData.ac?.problem === "not_tested" ||
    (testData.ac?.problem === "yes" &&
      testData.ac?.issue &&
      (testData.ac.issue !== "Annat" ||
        (testData.ac.issue === "Annat" &&
          testData.ac.other &&
          testData.ac.other.trim())));
  const isDashboardCompleted =
    testData.dashboard?.problem === "no" ||
    (testData.dashboard?.problem === "yes" &&
      testData.dashboard?.issue &&
      (testData.dashboard.issue !== "Annat" ||
        (testData.dashboard.issue === "Annat" &&
          testData.dashboard.other &&
          testData.dashboard.other.trim())));
  const isWindshieldCompleted =
    testData.windshield?.problem === "no" ||
    testData.windshield?.problem === "stone_chip" ||
    (testData.windshield?.problem === "yes" &&
      testData.windshield?.description &&
      testData.windshield.description.trim());
  const isBrakesCompleted =
    testData.brakes?.frontEffectiveness && testData.brakes?.rearEffectiveness;
  const isTireDepthCompleted = (() => {
    if (!testData.tires) return false;

    // Check if all primary depths are the same (indicating "same for all" was used)
    const primary = testData.tires.primary;
    const allPrimarySame =
      primary?.frontDepth &&
      primary?.rearDepth &&
      primary?.frontDepth === primary?.rearDepth;

    // Primary completion logic matches TireDepthSection component
    const primaryComplete = allPrimarySame
      ? !!primary?.frontDepth // If same for all, just need one value
      : !!primary?.frontDepth && !!primary?.rearDepth; // If different, need both front and rear

    // Secondary completion logic matches TireDepthSection component
    const secondaryComplete =
      !testData.tires.secondary ||
      (allPrimarySame
        ? !!testData.tires.secondary?.frontDepth // If same for all, just need one value
        : !!testData.tires.secondary?.frontDepth &&
          !!testData.tires.secondary?.rearDepth); // If different, need both

    return primaryComplete && secondaryComplete;
  })();

  return (
    <>
      <div className="relative flex items-center justify-center mb-6">
        <ModalInfoVehicleTest />
      </div>

      <div className="space-y-4">
        <FormSection
          isOpen={openSections.speed}
          title={s.testSpeed}
          value={testData.speed ? testData.speed : null}
          onEdit={(e) => handleEditSection("speed", e)}
          isCompleted={isSpeedCompleted}
        >
          <SpeedSection
            speed={testData.speed}
            onSpeedChange={(speed) => handleTestChange("speed", speed)}
            onNext={() => handleNext("speed")}
          />
        </FormSection>

        {isSpeedCompleted && (
          <FormSection
            isOpen={openSections.vibrations}
            title={s.vibrations}
            value={
              testData.vibrations?.problem
                ? testData.vibrations.problem === "yes"
                  ? testData.vibrations.location
                    ? `${v.yes} - ${getOptionDisplay(testData.vibrations.location)}`
                    : v.yes
                  : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("vibrations", e)}
            isCompleted={isVibrationsCompleted}
          >
            <VibrationsSection
              vibrations={testData.vibrations}
              onVibrationsChange={(vibrations) =>
                handleTestChange("vibrations", vibrations)
              }
              onNext={() => handleNext("vibrations")}
            />
          </FormSection>
        )}

        {isVibrationsCompleted && (
          <FormSection
            isOpen={openSections.leaks}
            title={s.leaks}
            value={
              testData.leaks?.problem
                ? testData.leaks.problem === "yes"
                  ? testData.leaks.source
                    ? `${v.yes} - ${getOptionDisplay(testData.leaks.source)}`
                    : v.yes
                  : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("leaks", e)}
            isCompleted={isLeaksCompleted}
          >
            <LeaksSection
              leaks={testData.leaks}
              onLeaksChange={(leaks) => handleTestChange("leaks", leaks)}
              onNext={() => handleNext("leaks")}
            />
          </FormSection>
        )}

        {isLeaksCompleted && (
          <FormSection
            isOpen={openSections.noises}
            title={s.strangeSounds}
            value={
              testData.noises?.problem
                ? testData.noises.problem === "yes"
                  ? testData.noises.type
                    ? `${v.yes} - ${getOptionDisplay(testData.noises.type)}`
                    : v.yes
                  : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("noises", e)}
            isCompleted={isNoisesCompleted}
          >
            <NoisesSection
              noises={testData.noises}
              onNoisesChange={(noises) => handleTestChange("noises", noises)}
              onNext={() => handleNext("noises")}
            />
          </FormSection>
        )}

        {isNoisesCompleted && (
          <FormSection
            isOpen={openSections.gearbox}
            title={s.gearbox}
            value={
              testData.gearbox?.problem
                ? `${testData.gearbox.problem === "yes" ? v.yes : v.no}${
                    testData.gearbox.issue
                      ? ` - ${getOptionDisplay(testData.gearbox.issue)}`
                      : ""
                  }`
                : ""
            }
            onEdit={(e) => handleEditSection("gearbox", e)}
            isCompleted={isGearboxCompleted}
          >
            <GearboxSection
              gearbox={testData.gearbox}
              onGearboxChange={(gearbox) =>
                handleTestChange("gearbox", gearbox)
              }
              onNext={() => handleNext("gearbox")}
            />
          </FormSection>
        )}

        {isGearboxCompleted && (
          <FormSection
            isOpen={openSections.crash}
            title={s.crashDamage}
            value={
              testData.crash?.problem
                ? testData.crash.problem === "yes"
                  ? testData.crash.area
                    ? `${v.yes} - ${getOptionDisplay(testData.crash.area)}`
                    : v.yes
                  : testData.crash.problem === "unknown"
                    ? v.unknown
                    : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("crash", e)}
            isCompleted={isCrashCompleted}
          >
            <CrashSection
              crash={testData.crash}
              onCrashChange={(crash) => handleTestChange("crash", crash)}
              onNext={() => handleNext("crash")}
            />
          </FormSection>
        )}

        {isCrashCompleted && (
          <FormSection
            isOpen={openSections.ac}
            title={s.ac}
            value={
              testData.ac?.problem
                ? testData.ac.problem === "yes"
                  ? testData.ac.issue
                    ? `${v.yes} - ${getOptionDisplay(testData.ac.issue)}`
                    : v.yes
                  : testData.ac.problem === "not_tested"
                    ? v.notTested
                    : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("ac", e)}
            isCompleted={isACCompleted}
          >
            <ACSection
              ac={testData.ac}
              onACChange={(ac) => handleTestChange("ac", ac)}
              onNext={() => handleNext("ac")}
            />
          </FormSection>
        )}

        {isACCompleted && (
          <FormSection
            isOpen={openSections.dashboard}
            title={s.dashboard}
            value={
              testData.dashboard?.problem
                ? testData.dashboard.problem === "yes"
                  ? testData.dashboard.issue
                    ? `${v.yes} - ${getOptionDisplay(testData.dashboard.issue)}`
                    : v.yes
                  : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("dashboard", e)}
            isCompleted={isDashboardCompleted}
          >
            <DashboardSection
              dashboard={testData.dashboard}
              onDashboardChange={(dashboard) =>
                handleTestChange("dashboard", dashboard)
              }
              onNext={() => handleNext("dashboard")}
            />
          </FormSection>
        )}

        {isDashboardCompleted && (
          <FormSection
            isOpen={openSections.windshield}
            title={s.windshield}
            value={
              testData.windshield?.problem
                ? testData.windshield.problem === "yes"
                  ? testData.windshield.description
                    ? `${v.yes} - ${testData.windshield.description}`
                    : v.yes
                  : testData.windshield.problem === "stone_chip"
                    ? v.stoneChip
                    : v.no
                : ""
            }
            onEdit={(e) => handleEditSection("windshield", e)}
            isCompleted={isWindshieldCompleted}
          >
            <WindshieldSection
              windshield={testData.windshield}
              onWindshieldChange={(windshield) =>
                handleTestChange("windshield", windshield)
              }
              onNext={() => handleNext("windshield")}
            />
          </FormSection>
        )}

        {isWindshieldCompleted && (
          <FormSection
            isOpen={openSections.brakes}
            title={s.brakes}
            value={
              testData.brakes?.frontEffectiveness &&
              testData.brakes?.rearEffectiveness
                ? testData.brakes.frontEffectiveness ===
                  testData.brakes.rearEffectiveness
                  ? `${testData.brakes.frontEffectiveness}`
                  : `${testData.brakes.frontEffectiveness} / ${testData.brakes.rearEffectiveness}`
                : ""
            }
            onEdit={(e) => handleEditSection("brakes", e)}
            isCompleted={isBrakesCompleted}
          >
            <BrakesSection
              brakes={testData.brakes}
              onBrakesChange={(brakes) => handleTestChange("brakes", brakes)}
            />
          </FormSection>
        )}

        {isBrakesCompleted && (
          <FormSection
            isOpen={openSections.tires}
            title={lp.vehicleTest.treadDepth}
            value={(() => {
              const primaryValue =
                testData.tires?.primary?.frontDepth ||
                testData.tires?.primary?.rearDepth ||
                "";

              const secondaryDepth =
                testData.tires?.secondary?.frontDepth ||
                testData.tires?.secondary?.rearDepth;
              const secondaryValue =
                secondaryDepth && secondaryDepth !== null
                  ? ` | ${v.extra}: ${secondaryDepth}`
                  : "";

              return primaryValue + secondaryValue;
            })()}
            onEdit={(e) => handleEditSection("tires", e)}
            isCompleted={isTireDepthCompleted}
          >
            <TireDepthSection
              tireDepth={(() => {
                // Convert tires structure to tireDepth structure for the component
                if (!testData.tires) {
                  // If tires doesn't exist, create initial structure with hasExtraSet from vehicleDetails
                  // In edit mode: Strictly use vehicleDetails?.extraTireSet from database
                  // In create mode: Use vehicleDetails?.extraTireSet from Fordonsinfo
                  const hasExtraSetValue =
                    vehicleDetails?.extraTireSet === true;
                  return {
                    sameForAll: true,
                    mainDepth: undefined,
                    frontDepth: undefined,
                    rearDepth: undefined,
                    hasExtraSet: hasExtraSetValue,
                    extraDepth: undefined,
                    extraFrontDepth: undefined,
                    extraRearDepth: undefined,
                  };
                }

                // Check if all primary depths are the same (indicating "same for all" was used)
                const primary = testData.tires.primary;
                const allPrimarySame =
                  primary?.frontDepth &&
                  primary?.frontDepth === primary?.rearDepth;

                // In edit mode: Strictly use vehicleDetails?.extraTireSet from database
                // In create mode: Use vehicleDetails?.extraTireSet from Fordonsinfo (can be undefined if not set)
                // This ensures that when editing a test, the checkbox reflects the saved extraTireSet value
                // If extraTireSet is false in the database, it should be false here, not inferred from secondary tires
                let hasExtraSetValue: boolean;
                if (isEditMode) {
                  // Edit mode: Strictly use vehicleDetails?.extraTireSet (or false if undefined)
                  // Never infer from testData.tires.secondary in edit mode
                  hasExtraSetValue = vehicleDetails?.extraTireSet === true;
                } else {
                  // Create mode: Use vehicleDetails?.extraTireSet from Fordonsinfo
                  // If undefined, default to false (user hasn't checked it in Fordonsinfo)
                  hasExtraSetValue = vehicleDetails?.extraTireSet === true;
                }

                return {
                  sameForAll: allPrimarySame,
                  mainDepth: allPrimarySame ? primary?.frontDepth : undefined,
                  frontDepth: allPrimarySame ? undefined : primary?.frontDepth,
                  rearDepth: allPrimarySame ? undefined : primary?.rearDepth,
                  hasExtraSet: hasExtraSetValue,
                  extraDepth: testData.tires.secondary?.frontDepth,
                  extraFrontDepth: testData.tires.secondary?.frontDepth,
                  extraRearDepth: testData.tires.secondary?.rearDepth,
                };
              })()}
              onTireDepthChange={(tireDepth) => {
                // Convert tireDepth structure back to tires structure
                const tiresData = {
                  primary: {
                    frontDepth: tireDepth?.frontDepth || tireDepth?.mainDepth,
                    rearDepth: tireDepth?.rearDepth || tireDepth?.mainDepth,
                  },
                  secondary: tireDepth?.hasExtraSet
                    ? {
                        frontDepth:
                          tireDepth?.extraFrontDepth || tireDepth?.extraDepth,
                        rearDepth:
                          tireDepth?.extraRearDepth || tireDepth?.extraDepth,
                      }
                    : undefined,
                };
                handleTestChange("tires", tiresData);

                // Sync hasExtraSet change back to vehicleDetails
                if (
                  onVehicleDetailsChange &&
                  tireDepth?.hasExtraSet !== undefined
                ) {
                  onVehicleDetailsChange({
                    extraTireSet: tireDepth.hasExtraSet,
                  });
                }
              }}
              onNext={onNext}
              isCreatingTest={isCreatingTest}
            />
          </FormSection>
        )}
      </div>
    </>
  );
};
